import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnDestroy,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconModule } from '@angular/material/icon'
import { Store } from '@ngrx/store'
import { AppState } from 'src/app/ngrx/states'
import { getTheme } from 'src/app/ngrx/store/selectors'

interface DotTheme {
  background: string
  dotBase: string
  dotWave: string
  textColor: string
  iconColor: string
}

const THEMES: Record<string, DotTheme> = {
  dark: {
    background: 'rgb(48, 48, 48)',
    dotBase: 'rgba(255, 255, 255, 0.08)',
    dotWave: 'rgba(255, 255, 255, 0.35)',
    textColor: '#777',
    iconColor: '#555',
  },
  light: {
    background: 'rgb(235, 233, 230)',
    dotBase: 'rgba(0, 0, 0, 0.08)',
    dotWave: 'rgba(0, 0, 0, 0.3)',
    textColor: '#999',
    iconColor: '#ccc',
  },
  paper: {
    background: 'rgb(228, 200, 159)',
    dotBase: 'rgba(80, 50, 20, 0.1)',
    dotWave: 'rgba(80, 50, 20, 0.35)',
    textColor: '#8b7355',
    iconColor: '#b09a78',
  },
}

@Component({
  selector: 'canvas-placeholder',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <canvas #dotCanvas></canvas>
    @if (showContent()) {
      <div class="placeholder-content">
        <mat-icon class="placeholder-icon">dashboard</mat-icon>
        <p>Select a canvas from the sidebar or create a new one</p>
      </div>
    }
  `,
  styles: `
    :host {
      display: flex;
      position: relative;
      flex: 1;
      height: 100%;
      overflow: hidden;
    }

    canvas {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }

    .placeholder-content {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
      pointer-events: none;
      z-index: 1;
    }

    .placeholder-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      opacity: 0.4;
    }

    p {
      font-size: 16px;
      margin: 0;
      opacity: 0.5;
    }
  `,
  imports: [MatIconModule],
})
export class CanvasPlaceholderComponent implements AfterViewInit, OnDestroy {
  readonly showContent = input<boolean>(true)

  private readonly store = inject(Store<AppState>)
  private readonly destroyRef = inject(DestroyRef)

  private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('dotCanvas')

  private animationId = 0
  private theme: DotTheme = THEMES['dark']

  private readonly DOT_SPACING = 28
  private readonly DOT_RADIUS = 1.5
  private readonly WAVE_INTERVAL = 3000 // ms between waves
  private readonly WAVE_DURATION = 2000 // ms for wave to reach edge
  private readonly WAVE_WIDTH = 120 // px width of the wave ring

  ngAfterViewInit(): void {
    this.store
      .select(getTheme)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(theme => {
        if (theme?.includes('paper')) {
          this.theme = THEMES['paper']
        } else if (theme?.includes('light')) {
          this.theme = THEMES['light']
        } else {
          this.theme = THEMES['dark']
        }
        this.updateContentColors()
      })

    this.setupCanvas()
    this.startAnimation()

    const ro = new ResizeObserver(() => this.setupCanvas())
    ro.observe(this.canvasRef().nativeElement)
    this.destroyRef.onDestroy(() => ro.disconnect())
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationId)
  }

  private updateContentColors(): void {
    if (!this.showContent()) return
    const host = this.canvasRef().nativeElement.parentElement
    if (!host) return
    const content = host.querySelector('.placeholder-content') as HTMLElement
    if (!content) return
    const icon = content.querySelector('.placeholder-icon') as HTMLElement
    const p = content.querySelector('p') as HTMLElement
    if (icon) icon.style.color = this.theme.iconColor
    if (p) p.style.color = this.theme.textColor
  }

  private setupCanvas(): void {
    const canvas = this.canvasRef().nativeElement
    const rect = canvas.getBoundingClientRect()
    const dpr = window.devicePixelRatio || 1
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  private startAnimation(): void {
    const canvas = this.canvasRef().nativeElement
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const animate = (timestamp: number): void => {
      this.drawFrame(ctx, canvas, timestamp)
      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  private drawFrame(
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement,
    timestamp: number,
  ): void {
    const w = canvas.clientWidth
    const h = canvas.clientHeight
    if (w === 0 || h === 0) return

    // Background
    ctx.fillStyle = this.theme.background
    ctx.fillRect(0, 0, w, h)

    const cx = w / 2
    const cy = h / 2
    const maxDist = Math.sqrt(cx * cx + cy * cy)

    // Wave progress: cycles every WAVE_INTERVAL, wave itself lasts WAVE_DURATION
    const cycleTime = timestamp % this.WAVE_INTERVAL
    const waveProgress = cycleTime / this.WAVE_DURATION // 0..1 during wave, >1 means wave done
    const waveRadius = waveProgress * maxDist // current wave front distance from center

    // Draw dots
    const spacing = this.DOT_SPACING
    const startX = cx % spacing
    const startY = cy % spacing

    for (let x = startX; x < w; x += spacing) {
      for (let y = startY; y < h; y += spacing) {
        const dx = x - cx
        const dy = y - cy
        const dist = Math.sqrt(dx * dx + dy * dy)

        // Calculate wave influence on this dot
        let intensity = 0
        if (waveProgress <= 1.0 + this.WAVE_WIDTH / maxDist) {
          const distFromWaveFront = Math.abs(dist - waveRadius)
          if (distFromWaveFront < this.WAVE_WIDTH) {
            intensity = 1 - distFromWaveFront / this.WAVE_WIDTH
            // Smooth falloff
            intensity = intensity * intensity * (3 - 2 * intensity)
            // Fade out as wave expands
            const fadeFactor = 1 - Math.min(waveRadius / maxDist, 1)
            intensity *= 0.3 + 0.7 * fadeFactor
          }
        }

        const radius = this.DOT_RADIUS + intensity * 2.2
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, Math.PI * 2)

        if (intensity > 0) {
          ctx.fillStyle = this.lerpColor(this.theme.dotBase, this.theme.dotWave, intensity)
        } else {
          ctx.fillStyle = this.theme.dotBase
        }
        ctx.fill()
      }
    }
  }

  private lerpColor(colorA: string, colorB: string, t: number): string {
    const a = this.parseRgba(colorA)
    const b = this.parseRgba(colorB)
    const r = Math.round(a.r + (b.r - a.r) * t)
    const g = Math.round(a.g + (b.g - a.g) * t)
    const bl = Math.round(a.b + (b.b - a.b) * t)
    const alpha = a.a + (b.a - a.a) * t
    return `rgba(${r}, ${g}, ${bl}, ${alpha})`
  }

  private parseRgba(color: string): { r: number; g: number; b: number; a: number } {
    const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
    if (!match) return { r: 0, g: 0, b: 0, a: 1 }
    return {
      r: parseInt(match[1]),
      g: parseInt(match[2]),
      b: parseInt(match[3]),
      a: match[4] !== undefined ? parseFloat(match[4]) : 1,
    }
  }
}
